import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from '../../lib/toast';
import Icon from '../../components/dashboard/DashboardIcons';
import { useParentData } from '../../context/ParentDataContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const SUB_LABELS = { active: 'نشط', expired: 'منتهي', suspended: 'موقوف', cancelled: 'ملغى' };
const SUB_STYLES = {
  active: 'bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]',
  expired: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#FECACA]',
  suspended: 'bg-[#FFFBEB] text-[#B45309] border border-[#FDE68A]',
};

export const ChildCard = ({ child }) => (
  <Link
    to={`/parent/children/${child.player_id}`}
    data-testid={`parent-child-card-${child.player_id}`}
    className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
  >
    <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-l from-teal-500 to-teal-700" />
    <div className="relative flex items-center gap-3 pt-2">
      {child.photo_url ? (
        <img src={child.photo_url} alt={child.player_name} className="h-16 w-16 rounded-2xl border-4 border-white object-cover shadow-md" />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl border-4 border-white bg-teal-100 text-xl font-black text-teal-700 shadow-md">{child.player_name?.charAt(0)}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-black text-slate-900" data-testid={`parent-child-name-${child.player_id}`}>{child.player_name}</div>
        <div className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-slate-500">
          {child.club_logo_file_id && <img src={`${API}/files/${child.club_logo_file_id}`} alt="" className="h-4 w-4 rounded object-contain" />}
          {child.club_name} {child.category_name ? `· ${child.category_name}` : ''}
        </div>
      </div>
    </div>
    <div className="mt-3 flex items-center justify-between">
      <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${SUB_STYLES[child.subscription_status] || 'bg-slate-100 text-slate-500'}`}>
        {SUB_LABELS[child.subscription_status] || 'غير محدد'}
      </span>
      <Icon name="chevronLeft" className="h-4 w-4 rotate-180 text-slate-300 transition-transform group-hover:-translate-x-1 group-hover:text-teal-500" />
    </div>
    {child.next_training ? (
      <div className="mt-3 flex items-center gap-2 rounded-xl bg-teal-50 px-3 py-2 text-xs font-semibold text-teal-700" data-testid={`parent-child-next-training-${child.player_id}`}>
        <Icon name="calendar" className="h-4 w-4" />
        أقرب تدريب: {child.next_training.title} — {child.next_training.date} {child.next_training.start_time || ''}
      </div>
    ) : (
      <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-xs text-slate-400">لا يوجد تدريب مجدول قريباً</div>
    )}
  </Link>
);

export default function ParentHomePage() {
  const parentData = useParentData();
  const navigate = useNavigate();
  const { childrenList: children, pendingLinkRequests: pending, refreshChildren } = parentData || {};

  useEffect(() => {
    const redeemPending = async () => {
      const token = localStorage.getItem('pending_parent_invite');
      if (!token) return;
      try {
        await axios.post(`${API}/parent/invite/${token}/redeem`, {}, auth());
        toast.success('تم ربط الابن بحسابك بنجاح');
      } catch { /* silently ignore expired/used token */ } finally {
        localStorage.removeItem('pending_parent_invite');
        refreshChildren && refreshChildren();
      }
    };
    redeemPending();
  }, [refreshChildren]);

  const displayName = JSON.parse(localStorage.getItem('user') || '{}')?.full_name || '';

  return (
    <div className="space-y-6" data-testid="parent-home-page">
      <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        <h1 className="text-2xl font-black text-slate-900">مرحباً {displayName} 👋</h1>
        <p className="mt-1 text-sm text-slate-500">تابع أبناءك واشتراكاتهم وأقرب مواعيدهم من مكان واحد</p>
      </div>

      {pending && pending.length > 0 && (
        <div className="rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4" data-testid="parent-pending-requests-banner">
          <div className="flex items-center gap-2 text-sm font-bold text-[#B45309]"><Icon name="clock" className="h-4 w-4" /> لديك {pending.length} طلب ربط بانتظار موافقة الأكاديمية</div>
          <p className="mt-1 text-xs text-[#B45309]/80">سنعلمك فوراً عند اعتماد الأكاديمية للطلب. راجع الحالة من صفحة «حسابي».</p>
        </div>
      )}

      {children === null || children === undefined ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {[1, 2].map((i) => <div key={i} className="h-40 animate-pulse rounded-3xl bg-slate-100" />)}
        </div>
      ) : children.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="parent-no-children-empty">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-100 text-teal-600"><Icon name="users" className="h-7 w-7" /></div>
          <p className="mt-4 text-sm font-bold text-slate-700">لا يوجد أبناء مرتبطين بحسابك بعد</p>
          <p className="mt-1 max-w-xs text-xs text-slate-400">إن كان ابنك مسجلاً في أكاديمية، تواصل معها لإرسال دعوة ربط، أو سجّله في أكاديمية جديدة من الاستكشاف</p>
          <button onClick={() => navigate('/parent/explore')} data-testid="parent-home-explore-cta" className="mt-4 rounded-2xl bg-teal-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:bg-teal-700 hover:shadow-md active:scale-95">استكشف الأكاديميات</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="parent-children-grid">
          {children.map((c) => <ChildCard key={c.player_id} child={c} />)}
        </div>
      )}
    </div>
  );
}
